import React from 'react';
import type { TExtendedSize } from '../../tokens/types';
import { Container } from './HBAvatarGroup.styles';
import { HBAvatarGroupContext } from './HBAvatarGroupContext';
import HBAvatar from '../HBAvatar/HBAvatar';

export interface HBAvatarGroupProps {
    /** Accessible label for the group container (role='group'). */
    ariaLabel?: string;
    /** HBAvatar elements to render; excess items beyond `maxItems` are replaced by an overflow indicator. */
    children?: React.ReactNode;
    /** Maximum total items to display (including overflow indicator). */
    maxItems?: number;
    /**
     * Customize the overflow indicator. Receives the default element (with "+X" text) and hidden count.
     * @param defaultElement The default overflow avatar with "+X" text and aria-label
     * @param count Number of hidden items
     */
    renderOverflow?: (
        defaultElement: React.ReactNode,
        count: number
    ) => React.ReactNode;
    /** Avatar size propagated via context to all child HBAvatar instances. @default 'sm' */
    size?: TExtendedSize;
    /** When true, avatars visually overlap each other with negative margin. */
    overlap?: boolean;
    /** CSS class applied to the root element. */
    className?: string;
    /** Test identifier applied as data-testid on the root element. */
    testId?: string;
}

const HBAvatarGroup = ({
    ariaLabel,
    children,
    className,
    maxItems = 10,
    renderOverflow,
    size = 'sm',
    overlap = true,
    testId
}: HBAvatarGroupProps) => {
    const childArray = React.Children.toArray(children);
    const totalChildren = childArray.length;

    // Ensure at least two avatars are shown for better UX
    const maxVisible = Math.max(2, maxItems);
    const showOverflow = totalChildren > maxVisible;
    const visibleCount = showOverflow ? maxVisible - 1 : totalChildren;
    const overflowCount = totalChildren - visibleCount;

    const visibleChildren = childArray.slice(0, visibleCount);

    const defaultOverflowElement = (
        <HBAvatar ariaLabel={`${overflowCount} more members`}>
            +{overflowCount}
        </HBAvatar>
    );

    return (
        <HBAvatarGroupContext.Provider value={{ size }}>
            <Container
                aria-label={ariaLabel}
                className={className}
                data-testid={testId}
                role="group"
                $overlap={overlap}
                $size={size}
            >
                {visibleChildren}
                {showOverflow &&
                    (renderOverflow
                        ? renderOverflow(defaultOverflowElement, overflowCount)
                        : defaultOverflowElement)}
            </Container>
        </HBAvatarGroupContext.Provider>
    );
};

export default HBAvatarGroup;
